import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type AppNotification } from '../api/notifications';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { formatDate } from '../utils/format';

function NotificationItem({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  return (
    <button
      onClick={() => !notification.read && onRead(notification.id)}
      className={[
        'w-full text-left px-4 py-3.5 border-b border-gray-100 last:border-0 transition-colors',
        notification.read ? 'bg-white' : 'bg-emerald-50 active:bg-emerald-100',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        {!notification.read && (
          <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500" />
        )}
        <div className={`flex-1 min-w-0 ${notification.read ? 'pl-5' : ''}`}>
          <p className={`text-sm font-semibold text-gray-900 ${notification.read ? 'font-medium' : ''}`}>
            {notification.title}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">{notification.body}</p>
          <p className="text-xs text-gray-400 mt-1">{formatDate(notification.createdAt)}</p>
        </div>
      </div>
    </button>
  );
}

export function NotificationsScreen() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data: notifications, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.list,
    staleTime: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: (_, id) => {
      queryClient.setQueryData<AppNotification[]>(['notifications'], (old) =>
        old ? old.map((n) => (n.id === id ? { ...n, read: true } : n)) : []
      );
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    },
    onError: () => addToast('Erro ao marcar notificação.', 'error'),
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      queryClient.setQueryData<AppNotification[]>(['notifications'], (old) =>
        old ? old.map((n) => ({ ...n, read: true })) : []
      );
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      addToast('Todas as notificações marcadas como lidas.', 'success');
    },
    onError: () => addToast('Erro ao marcar todas como lidas.', 'error'),
  });

  const unreadCount = notifications?.filter((n) => !n.read).length ?? 0;

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader
        title="Notificações"
        showBack
        onBack={() => navigate(-1)}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        action={
          unreadCount > 0 ? (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="text-sm font-semibold text-brand-600 disabled:opacity-50 touch-manipulation"
            >
              {markAllReadMutation.isPending ? '...' : 'Marcar todas'}
            </button>
          ) : undefined
        }
      />

      <div className="flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : !notifications || notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-5 text-center gap-3">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">Nenhuma notificação.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mx-4 mt-4 overflow-hidden">
            {notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onRead={(id) => markReadMutation.mutate(id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
